import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, message, Modal, Popconfirm, Select, Space, Switch, Table, Typography } from 'antd';
import api from '../../services/api';
import { isAppAdmin } from '../../services/access-policy';
import { useGlobalContext } from '../../store/GlobalContext';

type Account = { id: number; uid: string; email: string; firstName?: string; lastName?: string; roles?: string; isAdmin?: boolean; active?: boolean; accountLocked?: boolean };

export default function AdminAccountsPage() {
  const { auth } = useGlobalContext();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Account | null>(null);
  const [profileForm] = Form.useForm();
  const [busy, setBusy] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    try { setAccounts((await api.get('/user')).data); }
    catch (error: any) { message.error(error.response?.data?.message || 'Could not load accounts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isAppAdmin(auth.user)) void load(); }, [auth.user]);
  if (!isAppAdmin(auth.user)) return <Alert type="error" message="Administrator access required" />;
  const update = async (account: Account, changes: object) => {
    setBusy(account.uid);
    try { await api.patch(`/user/admin/${account.uid}/account`, changes); await load(); message.success('Account updated'); }
    catch (error: any) { message.error(error.response?.data?.message || 'Could not update account'); }
    finally { setBusy(null); }
  };
  const saveProfile = async (values: { firstName?: string; lastName?: string }) => {
    if (!editing) return;
    setBusy(editing.uid);
    try { await api.patch(`/user/${editing.id}`, values); setEditing(null); await load(); message.success('Profile updated'); }
    catch (error: any) { message.error(error.response?.data?.message || 'Could not update profile'); }
    finally { setBusy(null); }
  };
  const remove = async (account: Account) => {
    setBusy(account.uid);
    try { await api.delete(`/user/${account.uid}`); await load(); message.success('Account deleted'); }
    catch (error: any) { message.error(error.response?.data?.message || 'Could not delete account'); }
    finally { setBusy(null); }
  };
  return <Space direction="vertical" style={{ width: '100%' }} size="large">
    <Typography.Title level={2}>Account administration</Typography.Title>
    <Typography.Paragraph>Manage account access and app administrators. Executor permissions come from each estate's executor roster.</Typography.Paragraph>
    <Input.Search placeholder="Search name or email" value={search} onChange={event => setSearch(event.target.value)} allowClear />
    <Table rowKey="uid" loading={loading} dataSource={accounts.filter(account => `${account.email} ${account.firstName || ''} ${account.lastName || ''}`.toLowerCase().includes(search.toLowerCase()))} columns={[
      { title: 'Email', dataIndex: 'email' },
      { title: 'Name', render: (_, account) => `${account.firstName || ''} ${account.lastName || ''}`.trim() },
      { title: 'App role', render: (_, account) => <Select value={isAppAdmin(account) ? 'admin' : 'user'} disabled={busy === account.uid || auth.user?.uid === account.uid} options={[{ value: 'user', label: 'User' }, { value: 'admin', label: 'Administrator' }]} onChange={role => void update(account, { role })} /> },
      { title: 'Active', render: (_, account) => <Switch checked={account.active !== false} disabled={busy === account.uid || auth.user?.uid === account.uid} onChange={active => void update(account, { active })} /> },
      { title: 'Locked', render: (_, account) => <Switch checked={!!account.accountLocked} disabled={busy === account.uid || auth.user?.uid === account.uid} onChange={isLocked => void update(account, { isLocked })} /> },
      { title: 'Actions', render: (_, account) => <Space><Button onClick={() => { setEditing(account); profileForm.setFieldsValue({ firstName: account.firstName, lastName: account.lastName }); }}>Edit profile</Button><Popconfirm title="Delete this account?" description="Associated data may prevent deletion. Lock the account to immediately revoke access." onConfirm={() => remove(account)}><Button danger disabled={busy === account.uid || auth.user?.uid === account.uid}>Delete</Button></Popconfirm></Space> },
    ]} />
    <Modal title="Edit account profile" open={!!editing} onCancel={() => setEditing(null)} onOk={() => profileForm.submit()} confirmLoading={!!busy}>
      <Form form={profileForm} layout="vertical" onFinish={saveProfile}>
        <Form.Item name="firstName" label="First name"><Input maxLength={255} /></Form.Item>
        <Form.Item name="lastName" label="Last name"><Input maxLength={255} /></Form.Item>
      </Form>
    </Modal>
  </Space>;
}
